import { isSignedContainerFile } from "../constants/fileTypes";
import type { TreeFolder } from "../types";

/** A container document as returned by `fetchProjectContainers`. */
export interface ProjectContainer {
  id: string;
  filename: string;
  folderId: string | null;
  childCount: number;
}

/**
 * Inject container documents into the folder tree as virtual folder entries, so
 * a `.zip` can be navigated into from the sidebar.
 *
 * Signed containers (.asice/.bdoc/.ddoc) are deliberately **excluded**: an
 * e-signature container is a sealed file, not a folder, and showing it in the
 * folder navigation is what made users click into it expecting a folder and
 * land on a single document with no sign of the signatures. They render as a
 * signed-container row in the file list instead (`SignedContainerRow`).
 */
export function injectContainerFolders(
  tree: TreeFolder[],
  containers: ProjectContainer[],
  buildingId: string
): TreeFolder[] {
  const navigable = containers.filter((c) => !isSignedContainerFile(c.filename));
  if (navigable.length === 0) return tree;

  const containersByFolder = new Map<string, ProjectContainer[]>();
  for (const container of navigable) {
    const key = container.folderId ?? "root";
    const bucket = containersByFolder.get(key);
    if (bucket) bucket.push(container);
    else containersByFolder.set(key, [container]);
  }

  const toVirtualFolder = (c: ProjectContainer): TreeFolder => ({
    id: `container-${c.id}`,
    name: c.filename,
    parentId: c.folderId,
    category: "documents",
    isCustom: false,
    building_id: Number(buildingId),
    children: [],
    documentCount: c.childCount,
    isContainer: true,
    containerId: c.id,
  });

  const injectInto = (folders: TreeFolder[]): TreeFolder[] =>
    folders.map((folder) => ({
      ...folder,
      children: [
        ...injectInto(folder.children),
        ...(containersByFolder.get(folder.id) ?? []).map(toVirtualFolder),
      ],
    }));

  const rootContainers = containersByFolder.get("root") ?? [];
  return [...injectInto(tree), ...rootContainers.map(toVirtualFolder)];
}
